import CabinIcon from '@mui/icons-material/Cabin'
import LoginIcon from '@mui/icons-material/Login'
import ReplayIcon from '@mui/icons-material/Replay'
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  Drawer,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import dayjs from 'dayjs'
import { useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { CheckoutButtonComponent } from 'src/clinic/components/CheckIn/checkInComponents'
import { IdlePopupaStyledDialog } from 'src/clinic/components/CheckIn/components/styledComponents'
import {
  checkIntoRoomWithLocalStorageApplied,
  refreshCheckIntoRoomWithLocalStorageApplied,
} from 'src/clinic/components/CheckIn/services'
import { DISPATCH_EVENT_KEYS } from 'src/consumer/constants'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { sleep } from 'src/mentor/helpers/utilityFunctions'

export default function ValidateCheckInPopup({ currentClinicLocationData }) {
  const { mentorProfileData } = useContext(MentorProfileContext)
  const [checkingIn, setCheckingIn] = useState(false)
  const [showPopup, setShowPopup] = useState(false)
  const hasShownRef = useRef(false) // Use ref instead of state to track if shown
  const isMountedRef = useRef(true)


  // Reactive localStorage reading with state
  const [localStorageData, setLocalStorageData] = useState(() => {
    try {
      const item = localStorage.getItem('checkedInDetails')
      const peekData = localStorage.getItem('peekedInRoomDetails')
      return {
        checkedInDetails: item ? JSON.parse(item) : null,
        peekedInDetails: peekData ? JSON.parse(peekData) : null,
      }
    } catch {
      return { checkedInDetails: null, peekedInDetails: null }
    }
  })

  // Update localStorage data reactively
  useEffect(() => {
    const updateLocalStorageData = () => {
      try {
        const item = localStorage.getItem('checkedInDetails')
        const peekData = localStorage.getItem('peekedInRoomDetails')
        setLocalStorageData({
          checkedInDetails: item ? JSON.parse(item) : null,
          peekedInDetails: peekData ? JSON.parse(peekData) : null,
        })
      } catch (error) {
        console.error('Error parsing localStorage:', error)
      }
    }

    // Listen for storage events (from other tabs/windows)
    const handleStorageChange = (e) => {
      if (e.key === 'checkedInDetails' || e.key === 'peekedInRoomDetails') {
        updateLocalStorageData()
      }
    }

    // Listen for custom check-in events
    const handleCheckInEvent = async (event) => {
      await sleep(500)
      updateLocalStorageData()
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener(DISPATCH_EVENT_KEYS.userCheckedIn, handleCheckInEvent)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener(DISPATCH_EVENT_KEYS.userCheckedIn, handleCheckInEvent)
    }
  }, [showPopup])

  // Compute derived values from reactive state
  const parsedItem = localStorageData.checkedInDetails
  const peekedRoomData = localStorageData.peekedInDetails
  const hasCheckedInBefore = !!parsedItem
  const hasCheckedInOneDayBefore = useMemo(() => {
    if (!parsedItem?.createdTime) return false
    const result = dayjs(parsedItem.createdTime).isBefore(dayjs().subtract(1, 'day'))
    return result
  }, [parsedItem?.createdTime])

  const currentRoomName = parsedItem?.roomName || peekedRoomData?.title

  // Show popup logic - only show once per mount
  useEffect(() => {
    if (!isMountedRef.current) return


    // Only show if conditions are met and we haven't shown it yet
    if (hasCheckedInOneDayBefore && !showPopup && !hasShownRef.current) {
      setShowPopup(true)
      hasShownRef.current = true
    }
  }, [hasCheckedInOneDayBefore, showPopup])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const handleClose = useCallback((event, reason) => {
    // Prevent closing on backdrop click or escape key
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return
    }
    // Only allow programmatic closing (via Cancel button)
    setShowPopup(false)
  }, [])

  const handleCancel = useCallback(() => {
    // Clear localStorage and close popup
    localStorage.removeItem('checkedInDetails')
    localStorage.removeItem('peekedInRoomDetails')
    localStorage.removeItem('roomDataEventTypes')
    setShowPopup(false)
  }, [])

  const handlePrimaryAction = useCallback(async () => {
    setCheckingIn(true)
    try {
      if (hasCheckedInBefore) {
        await refreshCheckIntoRoomWithLocalStorageApplied(parsedItem)
      } else {
        await checkIntoRoomWithLocalStorageApplied({
          ...peekedRoomData,
          checkinMethod: 'check-in-method-in-person',
        })
      }
      // Close popup after successful check-in
      setShowPopup(false)
    } catch (error) {
      console.error('Error during check-in:', error)
      // Keep popup open on error so user can retry
    } finally {
      setCheckingIn(false)
    }
  }, [hasCheckedInBefore, parsedItem, peekedRoomData])

  // Don't render if no data or already handled
  if (!hasCheckedInOneDayBefore && !showPopup) {
    return null
  }

  // useEffect(() => {
  //   resetIdle()
  // }, [parsedItem])

  // return (
  //   <IdlePopupaStyledDialog open={isIdle}>
  //     <DialogTitle>Welcome to {currentClinicLocationData?.title}</DialogTitle>
  //     <DialogContent>
  //       {/* {(hasCheckedInOneDayBefore || !hasCheckedInBefore) && (
  //       <MentorAssociationClinicSelectionBox
  //         showCheckInPerson
  //         // hideClinicRelatedInfo
  //         hidePatientListingButton
  //         hideEmrPatientButton
  //         isolatedChamberRoomId={checkedInOrPeekedInChamberRoomId}
  //         clinicLocationData={currentClinicLocationData}
  //         containerCustomStyles={{ background: 'transparent', border: 'none', boxShadow: 'none' }}
  //         onChoosingInPerson={async () => {
  //           resetIdle()
  //         }}
  //         onChoosingPeekIn={async () => {
  //           resetIdle()
  //         }}
  //         onChoosingRemote={async () => {
  //           resetIdle()
  //         }}
  //       />
  //     )} */}
  //     </DialogContent>
  //     <DialogActions sx={{ justifyContent: 'center' }}>
  //       <CheckoutButtonComponent
  //         additionalFunctionAfterAction={() => resetIdle()}
  //         buttonVariant="outlined"
  //         text="Go back to Dashboard"
  //         exitButtonText="Go back to Dashboard"
  //         showExitButtonIfNotCheckedIn
  //         styleProp={{ textTransform: 'uppercase' }}
  //         buttonSize="medium"
  //         startIcon={<CabinIcon />}
  //       />
  //       {/* <Button
  //               variant="outlined"
  //               startIcon={<CabinIcon />}
  //               onClick={() => {
  //                 resetIdle()
  //                 if (!parsedItem) {
  //                 }
  //                 navigate('/mentor-home/my-clinic-associations')
  //               }}
  //             >
  //               Go back to Dashboard
  //             </Button> */}
  //       {hasCheckedInBefore && !hasCheckedInOneDayBefore && (
  //         <Button
  //           variant="contained"
  //           onClick={() => {
  //             refreshCheckIntoRoom(parsedItem)
  //             resetIdle()
  //           }}
  //         >
  //           Click/Tap to Continue
  //         </Button>
  //       )}
  //       {hasCheckedInOneDayBefore && (
  //         <Button
  //           variant="contained"
  //           onClick={() => {
  //             refreshCheckIntoRoom(parsedItem)
  //             resetIdle()
  //           }}
  //         >
  //           Re Checkin
  //         </Button>
  //       )}
  //       {!hasCheckedInBefore && (
  //         <Button
  //           disabled={!isInPersonCheckInAvailable}
  //           onClick={() => {
  //             checkIntoRoomWithLocalStorageApplied({
  //               ...peekedRoomData,
  //               checkinMethod: 'check-in-method-in-person',
  //             })
  //             resetIdle()
  //           }}
  //           variant="contained"
  //         >
  //           {isInPersonCheckInAvailable ? 'CheckIn' : 'You need go to dashboard to check in'}
  //         </Button>
  //       )}
  //     </DialogActions>
  //   </IdlePopupaStyledDialog>
  // )


  return (
    <PeekInPopup
      open={showPopup}
      chamberRoom={currentRoomName}
      clinicBrand={currentClinicLocationData?.clinicBrandName}
      clinicName={currentClinicLocationData?.title}
      mentorName={mentorProfileData?.name}
      type={hasCheckedInBefore ? 're-checkin' : 'peek-in'}
      showLoaderOnPrimaryAction={checkingIn}
      onPrimaryAction={handlePrimaryAction}
      onCancel={handleCancel}
      onClose={handleClose}
    />
  )
}

const PeekInPopup = ({
  open,
  type, // 'peek-in' | 're-checkin'
  clinicBrand,
  clinicName,
  mentorName,
  chamberRoom,
  onPrimaryAction,
  showLoaderOnPrimaryAction,
  onCancel,
  onClose, // Add onClose prop
}) => {
  const isPeekIn = type === 'peek-in'
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))


  // Handle close with proper prevention
  const handleClose = useCallback((event, reason) => {
    // Prevent closing on backdrop click or escape key
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return
    }
    // Only allow programmatic closing
    if (onClose) {
      onClose(event, reason)
    }
  }, [onClose, open])

  // Shared content component
  const renderContent = () => (
    <>
      <DialogTitle sx={{ padding: { xs: '18px 16px 14px', sm: '22px 24px 18px' } }}>
        <Stack alignItems="center" spacing={0.5}>
          <Typography variant="h6" fontWeight={600} align="center" sx={{ fontSize: { xs: '16px', sm: '17px' }, color: '#0f172a', letterSpacing: '-0.01em' }}>
            {mentorName}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ padding: { xs: '20px 16px', sm: '24px 24px' }, overflowY: 'auto', pb: { xs: '24px', sm: '28px' } }}>
        <Box textAlign="center" mb={{ xs: 2.5, sm: 3 }}>
          <Typography variant="body2" sx={{ fontSize: { xs: '13px', sm: '14px' }, mb: 0.75, color: '#475569', lineHeight: 1.5 }}>
            <strong style={{ color: '#0f172a', fontWeight: 600 }}>Clinic:</strong> {clinicBrand} {clinicName}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: { xs: '13px', sm: '14px' }, color: '#475569', lineHeight: 1.5 }}>
            <strong style={{ color: '#0f172a', fontWeight: 600 }}>Room:</strong> {chamberRoom}
          </Typography>
        </Box>
        <Typography 
          variant="body2" 
          textAlign="center" 
          sx={{ 
            fontSize: { xs: '13px', sm: '14px' },
            color: '#475569',
            lineHeight: 1.6,
            mb: { xs: 0.5, sm: 1 },
            fontWeight: 400,
          }}
        >
          {isPeekIn
            ? "You haven't checked in yet. Proceed with check-in?"
            : "Your last check-in was from a previous day. Check in again?"}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ 
        justifyContent: 'center', 
        padding: { xs: '16px', sm: '20px 24px' }, 
        paddingTop: { xs: '12px', sm: '16px' },
        flexShrink: 0, 
        gap: { xs: 1.25, sm: 1.5 },
      }}>
        <CheckoutButtonComponent
          additionalFunctionAfterAction={onCancel}
          buttonVariant="outlined"
          text="Cancel"
          exitButtonText="Cancel"
          showExitButtonIfNotCheckedIn
          styleProp={{ textTransform: 'none', fontWeight: 500, fontSize: { xs: '13px', sm: '14px' } }}
          buttonSize="medium"
          startIcon={<CabinIcon />}
        />

        <Button
          variant="contained"
          color="emrSecondary"
          size="medium"
          startIcon={isPeekIn ? <LoginIcon /> : <ReplayIcon />}
          sx={{ 
            position: 'relative', 
            color: 'white !important', 
            minWidth: { xs: 120, sm: 140 },
            fontWeight: 600,
            textTransform: 'none',
            fontSize: { xs: '13px', sm: '14px' },
            px: { xs: 2.5, sm: 3 },
            py: { xs: 1, sm: 1.25 },
          }}
          disabled={showLoaderOnPrimaryAction}
          onClick={onPrimaryAction}
        >
          {isPeekIn ? 'Check In' : 'Re-check In'}
          {showLoaderOnPrimaryAction && (
            <CircularProgress
              color="unFocused"
              size={16}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                marginTop: '-8px',
                marginLeft: '-8px',
              }}
            />
          )}
        </Button>
      </DialogActions>
    </>
  )

  // Mobile: Drawer from bottom
  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={handleClose} // Use handleClose instead of onCancel
        PaperProps={{
          sx: {
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '20px',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        sx={{
          zIndex: 1300,
        }}
      >
        {/* Drag handle indicator */}
        <Box
          sx={{
            width: '40px',
            height: '4px',
            backgroundColor: '#e2e8f0',
            borderRadius: '2px',
            margin: '8px auto',
            cursor: 'grab',
            '&:active': {
              cursor: 'grabbing',
            },
          }}
        />
        {renderContent()}
      </Drawer>
    )
  }

  // Desktop: Dialog with proper onClose handler
  return (
    <IdlePopupaStyledDialog 
      open={open} 
      onClose={handleClose} // Add onClose handler
      maxWidth="xs" 
      fullWidth
      scroll="paper"
      sx={{
        '& .MuiDialog-paper': {
          margin: { xs: '8px', sm: '16px' },
          maxHeight: { xs: '95vh', sm: '90vh' },
        },
      }}
      BackdropProps={{
        onClick: (e) => {
          e.stopPropagation()
          // Prevent backdrop click from closing
        },
        sx: {
          pointerEvents: 'auto',
        },
      }}
    >
      {renderContent()}
    </IdlePopupaStyledDialog>
  )
}
